import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { SearchOutlined } from "@mui/icons-material";
import { Box, Button, Checkbox, Chip, FormControlLabel, FormHelperText, InputAdornment, TextField, Typography } from "@mui/material";

// Never render more than this many checkboxes at once — a platform can have
// hundreds of orgs. Search narrows the list; "Select all" still applies to
// every match, not just the rows on screen.
const MAX_VISIBLE = 100;
const MAX_CHIPS = 6;

// Checkbox list for choosing several organizations (replaces the single-org
// dropdown on the Assign role drawer). Searchable, with Select all / Clear, a
// running count, and removable chips for what's selected so a choice stays
// visible (and removable) even when the search filters it out of the list.
const OrgCheckboxList = ({ orgs, selectedIds, onChange, error, helperText, isLoading = false, label = "Organizations", required = false, lockedIds = [] }) => {
  const [query, setQuery] = useState("");

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  // Orgs the user already has: shown ticked and locked, never part of the picks.
  const locked = useMemo(() => new Set(lockedIds), [lockedIds]);
  const sorted = useMemo(() => [...(orgs || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")), [orgs]);
  const nameById = useMemo(() => new Map(sorted.map((org) => [org._id, org.name])), [sorted]);

  const term = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (term ? sorted.filter((org) => (org.name || "").toLowerCase().includes(term)) : sorted),
    [sorted, term]
  );
  const visible = filtered.slice(0, MAX_VISIBLE);
  const pickable = filtered.filter((org) => !locked.has(org._id));
  const allFilteredSelected = pickable.every((org) => selected.has(org._id));

  const toggle = (id) => onChange(selected.has(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  const selectAllFiltered = () => onChange([...new Set([...selectedIds, ...pickable.map((org) => org._id)])]);

  return (
    <Box role="group" aria-label={label}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
          {required && " *"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {selectedIds.length} selected{locked.size > 0 && ` · ${locked.size} already assigned`}
        </Typography>
      </Box>

      <TextField
        size="small"
        fullWidth
        placeholder="Search organizations…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        inputProps={{ "aria-label": "Search organizations" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined fontSize="small" sx={{ color: "#9ca3af" }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 0.75 }}
      />

      <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
        <Button size="small" onClick={selectAllFiltered} disabled={pickable.length === 0 || allFilteredSelected} sx={{ textTransform: "none", minWidth: 0, px: 1 }}>
          {term ? `Select all ${pickable.length} shown` : `Select all (${pickable.length})`}
        </Button>
        <Button size="small" onClick={() => onChange([])} disabled={selectedIds.length === 0} sx={{ textTransform: "none", minWidth: 0, px: 1 }}>
          Clear
        </Button>
      </Box>

      <Box
        sx={{
          border: `1px solid ${error ? "#d32f2f" : "#e0e0e0"}`,
          borderRadius: "6px",
          maxHeight: 240,
          overflowY: "auto",
          px: 1,
          py: 0.25,
        }}
      >
        {isLoading ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
            Loading organizations…
          </Typography>
        ) : visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
            {term ? `No organization matches “${query.trim()}”.` : "No organizations available."}
          </Typography>
        ) : (
          visible.map((org) => (
            <FormControlLabel
              key={org._id}
              sx={{ display: "flex", mx: 0, "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
              disabled={locked.has(org._id)}
              control={<Checkbox size="small" checked={selected.has(org._id) || locked.has(org._id)} onChange={() => toggle(org._id)} />}
              label={
                locked.has(org._id) ? (
                  <>
                    {org.name}{" "}
                    <Typography component="span" variant="caption" color="text.secondary">
                      · Already assigned
                    </Typography>
                  </>
                ) : (
                  org.name
                )
              }
            />
          ))
        )}
      </Box>
      {filtered.length > MAX_VISIBLE && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Showing the first {MAX_VISIBLE} of {filtered.length} — search to narrow the list.
        </Typography>
      )}

      {selectedIds.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
          {selectedIds.slice(0, MAX_CHIPS).map((id) => (
            <Chip key={id} size="small" label={nameById.get(id) || id} onDelete={() => toggle(id)} />
          ))}
          {selectedIds.length > MAX_CHIPS && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
              +{selectedIds.length - MAX_CHIPS} more
            </Typography>
          )}
        </Box>
      )}

      {(error || helperText) && <FormHelperText error={Boolean(error)}>{error || helperText}</FormHelperText>}
    </Box>
  );
};

OrgCheckboxList.propTypes = {
  orgs: PropTypes.array,
  selectedIds: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  helperText: PropTypes.string,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  required: PropTypes.bool,
  lockedIds: PropTypes.array,
};

export default OrgCheckboxList;
